import { ApplicationConfig, inject, provideAppInitializer, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';

import { routes } from './app.routes';
import { appLanguages } from './app.languages';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { Amplify } from 'aws-amplify';
import { environment } from '../environments/environment';
import { authInterceptor } from './core/auth/auth.interceptor';
import { AuthService } from './core/auth/auth.service';

const isAmplifyConfigurable = (): boolean =>
    Boolean(environment.auth.userPoolId && environment.auth.userPoolClientId);

// Configure Amplify whenever pool IDs are present, even if `bypass` is on —
// `bypass` only short-circuits the guard and the interceptor, so the /login
// page must still be usable for exercising the OTP flow against a dev pool.
const configureAmplify = (): void => {
    if (!isAmplifyConfigurable()) {
        if (!environment.auth.bypass) {
            // eslint-disable-next-line no-console
            console.error(
                '[auth] environment.auth.userPoolId / userPoolClientId are not set. ' +
                    'Run `terraform output` and fill in environments/environment*.ts.',
            );
        }
        return;
    }
    Amplify.configure({
        Auth: {
            Cognito: {
                userPoolId: environment.auth.userPoolId,
                userPoolClientId: environment.auth.userPoolClientId,
            },
        },
    });
};

const restoreAuthSession = async (): Promise<void> => {
    if (!isAmplifyConfigurable()) return;
    await inject(AuthService).restoreSession();
};

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        provideRouter(routes),
        provideHttpClient(withInterceptors([authInterceptor])),
        // Order matters: Amplify must be configured before AuthService can talk to it.
        provideAppInitializer(configureAmplify),
        provideAppInitializer(restoreAuthSession),
        provideTranslateService({
            lang: appLanguages.he.lang,
            fallbackLang: appLanguages.he.lang,
            loader: provideTranslateHttpLoader({
                prefix: '/i18n/',
                suffix: '.json',
            }),
        }),
    ],
};
